// Store profile fields a seller edits at /sell and in the dashboard — docs/plans/marketplace-commerce.md §4
// journey 2 (name, handle/slug, tagline, categories, branding) and §3.1 `spaces`/`actors`.
import type { CategorySlug } from "../../api/types";
import { CATEGORY_SLUGS } from "../../lib/categories";
import { hasControlCharacter } from "../identity/chars";
import { DISPLAY_NAME_MAX_LENGTH } from "../identity/profile";

export const STORE_NAME_MAX_LENGTH = DISPLAY_NAME_MAX_LENGTH;
export const STORE_TAGLINE_MAX_LENGTH = 80;
export const STORE_DESCRIPTION_MAX_LENGTH = 1000;
export const STORE_CATEGORIES_MAX = 3;

/** Banner presets until sellers can upload images (M4). Keys are public/img families. */
export const STORE_BANNER_KEYS = ["c-dusk-village", "c-neon-harbor", "c-sky-islands", "c-lava-forge"] as const;
export type StoreBannerKey = (typeof STORE_BANNER_KEYS)[number];

export type StoreDetailsField = "name" | "tagline" | "description" | "categories" | "bannerKey";

export interface StoreDetails {
  name: string;
  tagline: string;
  description: string;
  categories: CategorySlug[];
  bannerKey: StoreBannerKey | null;
}

export type StoreDetailsCheck<T> = { ok: true; value: T } | { ok: false; field: StoreDetailsField; message: string };

function singleLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function checkText(
  field: StoreDetailsField,
  value: unknown,
  { max, required, multiline = false, label }: { max: number; required: boolean; multiline?: boolean; label: string },
): StoreDetailsCheck<string> {
  if (typeof value !== "string") return { ok: false, field, message: `${label} must be text.` };
  // Descriptions keep their line breaks; every other control character is refused.
  const text = multiline ? value.replace(/\r\n?/g, "\n").trim() : singleLine(value);
  const controls = multiline ? text.replace(/\n/g, "") : text;
  if (hasControlCharacter(controls)) return { ok: false, field, message: `${label} contains characters that aren't allowed.` };
  if (required && text.length === 0) return { ok: false, field, message: `Add a ${label.toLowerCase()}.` };
  if ([...text].length > max) return { ok: false, field, message: `${label} can be up to ${max} characters.` };
  return { ok: true, value: text };
}

const CHECKS: {
  [K in StoreDetailsField]: (value: unknown) => StoreDetailsCheck<StoreDetails[K]>;
} = {
  name: (value) => checkText("name", value, { max: STORE_NAME_MAX_LENGTH, required: true, label: "Store name" }),
  tagline: (value) => checkText("tagline", value, { max: STORE_TAGLINE_MAX_LENGTH, required: false, label: "Tagline" }),
  description: (value) =>
    checkText("description", value, {
      max: STORE_DESCRIPTION_MAX_LENGTH,
      required: false,
      multiline: true,
      label: "Description",
    }),
  categories: (value) => {
    if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
      return { ok: false, field: "categories", message: "Choose categories from the list." };
    }
    const unique = [...new Set(value)];
    if (!unique.every((item) => (CATEGORY_SLUGS as string[]).includes(item))) {
      return { ok: false, field: "categories", message: "Choose categories from the list." };
    }
    if (unique.length > STORE_CATEGORIES_MAX) {
      return { ok: false, field: "categories", message: `Choose up to ${STORE_CATEGORIES_MAX} categories.` };
    }
    return { ok: true, value: unique as CategorySlug[] };
  },
  bannerKey: (value) => {
    if (value === null) return { ok: true, value: null };
    if (typeof value === "string" && (STORE_BANNER_KEYS as readonly string[]).includes(value)) {
      return { ok: true, value: value as StoreBannerKey };
    }
    return { ok: false, field: "bannerKey", message: "Choose one of the banners." };
  },
};

/**
 * Validates the store fields present in `input` (a JSON body). With `requireAll`, every field must be present
 * (creation); otherwise only the given fields are checked (a partial edit). Unknown keys are ignored by the
 * caller's allow-list, never by this function silently accepting them.
 */
export function checkStoreDetails(
  input: Record<string, unknown>,
  { requireAll }: { requireAll: boolean },
): StoreDetailsCheck<Partial<StoreDetails>> {
  const value: Partial<StoreDetails> = {};
  for (const field of Object.keys(CHECKS) as StoreDetailsField[]) {
    if (!(field in input) || input[field] === undefined) {
      if (requireAll && field === "name") return { ok: false, field, message: "Add a store name." };
      continue;
    }
    const result = CHECKS[field](input[field]);
    if (!result.ok) return result;
    (value as Record<string, unknown>)[field] = result.value;
  }
  return { ok: true, value };
}

export type PublishRequirement = "name" | "tagline" | "categories";

/** What a draft still needs before it can go live (§4 journey 2). */
export function missingForPublish(store: Pick<StoreDetails, "name" | "tagline" | "categories">): PublishRequirement[] {
  const missing: PublishRequirement[] = [];
  if (singleLine(store.name).length === 0) missing.push("name");
  if (singleLine(store.tagline).length === 0) missing.push("tagline");
  if (store.categories.length === 0) missing.push("categories");
  return missing;
}
